import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { FlaskConical, Copy, Check, BookOpen, GitBranch } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { AnimatedWaves } from "@/components/AnimatedWaves";
import { PasswordInput } from "@/components/PasswordInput";

// Demo account panel is dev/staging only — set VITE_DEMO_MODE=true to show it.
// Never ship real credentials in the production bundle.
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD ?? "";
const DEMO_ACCOUNTS: Array<{
  email: string;
  nameEs: string;
  nameEn: string;
  labelEs: string;
  labelEn: string;
}> = [
  {
    email: "admin@costadorada1.com",
    nameEs: "Lyana Medina",
    nameEn: "Lyana Medina",
    labelEs: "Administrador",
    labelEn: "Administrator",
  },
  {
    email: "presidente@costadorada1.com",
    nameEs: "Bryan Miller · Presidente",
    nameEn: "Bryan Miller · President",
    labelEs: "Miembro de Junta",
    labelEn: "Board Member",
  },
  {
    email: "pedro.rivera@costadorada1.com",
    nameEs: "Pedro Rivera · Unidad J5",
    nameEn: "Pedro Rivera · Unit J5",
    labelEs: "Residente",
    labelEn: "Resident",
  },
];

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  async function performLogin(e: string, p: string) {
    setError(null);
    setSubmitting(true);
    try {
      await login(e, p);
      const dest = (location.state as any)?.from?.pathname ?? "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg =
        (err as AxiosError<{ message: string }>).response?.data?.message ??
        t("auth.invalidCredentials");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    await performLogin(email, password);
  }

  function useDemoAccount(demoEmail: string) {
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    performLogin(demoEmail, DEMO_PASSWORD);
  }

  function copyPassword() {
    navigator.clipboard.writeText(DEMO_PASSWORD).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function toggleLang() {
    i18n.changeLanguage(i18n.language.startsWith("es") ? "en" : "es");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient text-white px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-ocean-mist opacity-60" aria-hidden="true" />
      <AnimatedWaves position="bottom" tone="light" />

      <Link to="/" className="absolute top-6 left-6 text-sm text-primary-100 hover:text-white tracking-wider uppercase z-10">
        ← {tr("Volver al inicio", "Back home")}
      </Link>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="stacked" color="white" className="text-lg" />
          </div>
          <p className="text-primary-100/80 text-sm tracking-wider uppercase">{t("app.tagline")}</p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-elegant-lg p-8 border border-white/40">
          <h2 className="font-serif text-2xl text-primary-800 mb-1">{t("auth.welcome")}</h2>
          <div className="w-10 h-0.5 bg-dorado mb-6" />
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="label">{t("auth.email")}</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label">{t("auth.password")}</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-md font-medium tracking-wide transition shadow-elegant disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? t("auth.loggingIn") : t("auth.loginButton")}
            </button>
            <div className="flex items-center justify-between text-sm pt-2">
              <Link to="/forgot-password" className="text-primary-600 hover:text-primary-800 hover:underline">
                {t("auth.forgotPassword")}
              </Link>
              <button type="button" onClick={toggleLang} className="text-slate-500 hover:text-primary-700 tracking-wider uppercase text-xs">
                {i18n.language.startsWith("es") ? "English" : "Español"}
              </button>
            </div>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-200 text-center space-y-2">
            <Link
              to="/guia"
              className="inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-900 font-medium"
            >
              <BookOpen size={15} />
              {tr("¿Primera vez? Vea la guía visual del portal", "New here? See the visual guide")}
              <span aria-hidden="true">→</span>
            </Link>
            <Link
              to="/flujos"
              className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-primary-700 font-medium"
            >
              <GitBranch size={15} />
              {tr("¿Cómo fluye el trabajo entre roles?", "How does work flow between roles?")}
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          {DEMO_MODE && (
          <div className="mt-6 border-t border-slate-200 pt-5">
            <div className="bg-dorado-50 border border-dorado-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-dorado-700">
                  <FlaskConical size={16} />
                  <span className="font-medium text-sm tracking-wide">
                    {tr("Cuentas de prueba", "Demo accounts")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="text-xs inline-flex items-center gap-1 text-dorado-700 hover:text-dorado-900"
                  title={tr("Copiar contraseña", "Copy password")}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  <span className="font-mono">{DEMO_PASSWORD}</span>
                </button>
              </div>
              <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                {tr(
                  "Haga clic en una cuenta para entrar y explorar el sistema en ese rol.",
                  "Click an account to sign in and explore the system in that role."
                )}
              </p>
              <div className="space-y-1.5">
                {DEMO_ACCOUNTS.map((acc) => (
                  <button
                    key={acc.email}
                    type="button"
                    disabled={submitting}
                    onClick={() => useDemoAccount(acc.email)}
                    className="w-full text-left bg-white hover:bg-dorado-100 border border-dorado-200 rounded-md px-3 py-2 transition disabled:opacity-50 group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs uppercase tracking-wider text-dorado-700 font-medium">
                          {tr(acc.labelEs, acc.labelEn)}
                        </div>
                        <div className="text-sm text-primary-800 font-medium truncate">
                          {tr(acc.nameEs, acc.nameEn)}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{acc.email}</div>
                      </div>
                      <span className="text-xs text-dorado-700 group-hover:text-dorado-900 whitespace-nowrap">
                        {tr("Usar →", "Use →")}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}
        </div>

        <div className="text-center mt-6 text-sm text-primary-100">
          {tr("¿No tiene cuenta?", "Don't have an account?")}{" "}
          {tr(
            "El acceso lo emite la administración del condominio:",
            "Access is issued by the condominium administration:"
          )}{" "}
          <a
            href="mailto:admin@costadorada1.com"
            className="text-dorado-200 hover:text-white underline underline-offset-2"
          >
            admin@costadorada1.com
          </a>
        </div>
      </div>
    </div>
  );
}
